// The gateway's JSON endpoints (design 7 §2–§4). Cookies carry the session; state-changing requests
// are same-origin, so the browser's Origin header satisfies the gateway's check.

import type { PlaySettings } from "./game/auto";
import type { PublishedKey } from "./game/verify";
import { t } from "./strings";

/** A player's rating as the pages show it (design 14 §6.4, §6.5). */
export interface RatingView {
  placed: boolean;
  established: boolean;
  rating_x100: number;
  provisional_games: number;
  place?: number;
  flair?: string;
}

export interface Me {
  public_id: string;
  display_name: string;
  avatar: number;
  needs_profile: boolean;
  guest: boolean; // made by "Play now", not signed in (design 14 §9.2)
  rating: RatingView;
  settings: PlaySettings;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly body: Record<string, unknown> = {}) {
    super(message);
  }
}

/** The server's words as a sentence: pages show them as they are. */
const sentence = (s: string) => (s ? `${s[0]!.toUpperCase()}${s.slice(1)}${/[.!?]$/.test(s) ? "" : "."}` : t.serverError);

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let r: Response;
  try {
    r = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, t.noConnection); // never reached the server: status 0, and not the browser's "Failed to fetch"
  }
  if (r.status === 204) return undefined as T;
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new ApiError(r.status, sentence(String(data.error ?? r.statusText)), data);
  return data as T;
}

/** Stores the player's play settings (design 8 P3, P6) and returns them as kept. */
export async function setSettings(s: PlaySettings): Promise<PlaySettings> {
  return call<PlaySettings>("PUT", "/api/me/settings", s);
}

/** The signed-in account, or null when signed out. */
export async function me(): Promise<Me | null> {
  try {
    return await call<Me>("GET", "/api/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

/** The ways in (design 14 §9.5). */
export const authMethods = () => call<{ email: boolean }>("GET", "/auth/methods");
export const requestEmailLink = (email: string, then = "/") => call<{ sent: boolean }>("POST", "/auth/email", { email, then });
export const verifyEmailLink = (token: string) => call<Me>("POST", "/auth/email/verify", { token });

/** "Play now" (design 14 §9.2): a guest account and its session; a browser signed in keeps its account. */
export const startGuest = () => call<Me>("POST", "/api/guest");
export interface TableInvite {
  host: string;
  rules: string;
  length: string;
  clock?: string;
  seated: number;
  open: boolean;
  state: "open" | "full" | "playing" | "ended" | "closed";
  game_id?: string; // once ended: its replay is anyone's
}
export const tableInvite = (token: string) => call<TableInvite>("GET", `/api/tables/${encodeURIComponent(token)}/invite`);

export const setProfile = (displayName: string, avatar: number) => call<Me>("PUT", "/api/me/profile", { display_name: displayName, avatar });

export const signOut = () => call<void>("POST", "/auth/signout");

export type Rules = "wrc-2025" | "tenhou-4p-red";
export type Length = "east" | "east-south";
/** An unranked table's time setting (owner, 2026-10-12): 10 s + 20 s bank, 30 s + 60 s, or none. */
export type Clock = "standard" | "fast" | "relaxed" | "unlimited";

export interface Person {
  public_id: string;
  display_name: string;
  avatar: number;
}

export interface TableView {
  token: string;
  rules: Rules;
  length: Length;
  clock: Clock;
  state: "open" | "playing" | "ended" | "closed";
  creator: string;
  seats: (Person & { away?: boolean })[];
  seated: boolean;
  game_id?: string;
  expires?: number;
  next?: string; // the rematch's token, once it started
  kind: "private" | "rated" | "placement"; // design 14 §6
  end_reason?: string; // once ended: ended, aborted (the server stopped it), abandoned
}

/** A rematch proposal as a player sees it (design 14 §8.2). */
export interface Proposal {
  token: string; // the ended table's
  proposer_name: string;
  proposer: boolean;
  accepted: boolean; // this player has
  ready: number; // of the four, bots' seats counted
  votes: { public_id: string; name: string; accepted: boolean }[];
  ended?: string; // started, declined, withdrawn, ranked, table, address, failed
  ended_by?: string;
  next?: string;
}

export interface EndSeat {
  public_id?: string; // none for a bot
  name: string;
  avatar: number;
  placement: number;
  points: number;
  score_x1000: number;
  presence?: "here" | "elsewhere" | "gone";
  flair?: string;
  rated?: boolean;
  before_x100?: number;
  after_x100?: number;
  provisional?: boolean;
}

/** The end screen's state (design 14 §8.1). */
export interface TableEnd {
  token: string;
  kind: TableView["kind"];
  length: Length;
  game_id: string;
  settled: boolean;
  seats: EndSeat[];
  flair_changes?: { public_id: string; before: string; after: string }[];
}

export const rematches = (at: string | null) =>
  call<{ proposals: Proposal[]; end: TableEnd | null }>("GET", `/api/rematches${at ? `?at=${encodeURIComponent(at)}` : ""}`);

/** The ranked queue as the server tells it (design 14 §6.1). */
export interface QueueState {
  queued: boolean;
  length?: Length;
  waited_ms?: number;
  searching: Record<string, number>;
  forming?: boolean; // a table is forming for this player
  starts_ms?: number; // until it starts
  coming?: boolean; // this player has nothing to leave, or said "Join now"
  table?: string; // the table it was matched to, once started
}

export const queueState = () => call<QueueState>("GET", "/api/queue");
export const joinQueue = (length: Length) => call<QueueState>("POST", "/api/queue", { length });
export const leaveQueue = () => call<QueueState>("DELETE", "/api/queue");
export const joinQueueNow = () => call<QueueState>("POST", "/api/queue/now", {});
/** The placement game (design 14 §6.4): a 409 carries the table the account already sits at, if that is why. */
export const startPlacement = () => call<TableView>("POST", "/api/placement", {});

/** Opens a table; with bots it starts at once against three bots. A 409 carries the table the account sits at. */
export async function createTable(rules: Rules, length: Length, clock: Clock, bots: boolean): Promise<TableView> {
  return call<TableView>("POST", "/api/tables", { rules, length, clock, bots });
}

export const getTable = (token: string) => call<TableView>("GET", `/api/tables/${encodeURIComponent(token)}`);
/** A table's action; "rematch" proposes or accepts (design 14 §8.2) and answers the new table once all are in. */
/** The host gives another player's seat to a bot. */
export const removeSeat = (token: string, publicId: string) => call<TableView>("POST", `/api/tables/${encodeURIComponent(token)}/remove`, { public_id: publicId });
export const tableAction = (token: string, action: "join" | "leave" | "start" | "close" | "rematch" | "rematch-decline" | "abandon") =>
  call<TableView>("POST", `/api/tables/${encodeURIComponent(token)}/${action}`);

export interface LobbyLine {
  token: string;
  rules: Rules;
  length: Length;
  clock: Clock;
  creator: string;
  seated: number;
  created: number;
}

export const lobby = () => call<{ current: string; open: LobbyLine[] }>("GET", "/api/lobby");

export interface Friend extends Person {
  presence?: "online" | "playing" | "offline";
  table?: string;
  flair?: string;
}

export const friends = () => call<{ friends: Friend[]; incoming: Friend[]; outgoing: Friend[] }>("GET", "/api/friends");
export const requestFriend = (displayName: string) => call<{ accepted: boolean }>("POST", "/api/friends", { display_name: displayName });
export const acceptFriend = (publicId: string) => call<void>("POST", `/api/friends/${encodeURIComponent(publicId)}/accept`);
export const removeFriend = (publicId: string) => call<void>("DELETE", `/api/friends/${encodeURIComponent(publicId)}`);

export interface GameSeat {
  flair?: string;
  public_id?: string;
  display_name: string;
  bot: boolean;
  deleted?: boolean; // the account was deleted: no name, no profile
  placement: number;
  points: number;
  score_x1000: number;
}

export interface GameLine {
  game_id: string;
  rules: Rules;
  length: Length;
  ended: number;
  seats: GameSeat[];
}

/** A game of the history (design 14 §4): the player's seat in it and its review's state. */
export interface HistoryRow extends GameLine {
  seat: number;
  reviewed: boolean;
  graded?: number;
  accuracy_x100?: number; // the player's accuracy; absent without a review, or for one from before the summaries
  first_rating_x100?: number; // this game gave the player their first rating
  rating_change?: number; // a ranked game's change to the rating, hundredths
  provisional?: boolean;
}

/** A page of the player's games, newest first; `next` continues it ("" at the end). */
export const history = (filter: string, before: string, limit?: number) =>
  call<{ games: HistoryRow[]; next: string }>(
    "GET",
    `/api/games?${new URLSearchParams({ ...(filter ? { filter } : {}), ...(before ? { before } : {}), ...(limit ? { limit: String(limit) } : {}) }).toString()}`,
  );

export interface Profile extends Person {
  joined: number;
  self: boolean;
  guest: boolean;
  presence: "offline" | "online" | "playing";
  friends: (Person & { flair?: string })[];
  rating: RatingView;
  rating_history: { at: number; rating_x100: number }[];
  counts: { games: number; avg_placement: number; places: number[]; hands: number; wins: number; dealt_in: number; riichi: number; opened: number };
}

/** A page of anyone's games (design 14 §5), as history() does for the player's own. */
export const profileGames = (publicId: string, filter: string, before: string, limit?: number) =>
  call<{ games: HistoryRow[]; next: string }>(
    "GET",
    `/api/profiles/${encodeURIComponent(publicId)}/games?${new URLSearchParams({ ...(filter ? { filter } : {}), ...(before ? { before } : {}), ...(limit ? { limit: String(limit) } : {}) }).toString()}`,
  );

/** What a table's card shows of a player beyond the record (design 14 §7). */
export interface PlayerInfo extends RatingView {
  avatar: number;
}

export interface BoardRow {
  place: number;
  flair?: string;
  public_id: string;
  display_name: string;
  avatar: number;
  rating_x100: number;
  games: number;
  self?: boolean;
}
export interface Board {
  rows: BoardRow[];
  more: boolean;
  me?: BoardRow;
  provisional: number; // the viewer's provisional games played, 0 if none
}
export const leaderboard = (page: number) => call<Board>("GET", `/api/leaderboard?page=${page}`);
export const players = (ids: string[]) => call<Record<string, PlayerInfo>>("GET", `/api/players?ids=${ids.map(encodeURIComponent).join(",")}`);

export const profile = (publicId: string) => call<Profile>("GET", `/api/profiles/${encodeURIComponent(publicId)}`);

/** A finished game's signed record (mj.record.v1.GameRecord, binary); null when not visible. */
export async function gameRecord(gameId: string, canon = false): Promise<Uint8Array | null> {
  const r = await fetch(`/api/games/${encodeURIComponent(gameId)}/record${canon ? ".mjr" : ""}`, { credentials: "same-origin" });
  if (!r.ok) return null;
  return new Uint8Array(await r.arrayBuffer());
}

/** A game's review (design 12 §3): its state, as the gateway knows it. */
export interface ReviewState {
  state: "none" | "grading" | "ready" | "failed";
  reason?: "busy" | "unavailable" | "invalid" | "error" | "guest";
  engine?: string;
  graded?: number;
  created?: number;
}

export async function reviewState(gameId: string): Promise<ReviewState> {
  return call<ReviewState>("GET", `/api/games/${encodeURIComponent(gameId)}/review`);
}

/** Asks for the game's review; the state that follows ("grading", or "ready" when it exists). */
export async function requestReview(gameId: string): Promise<ReviewState> {
  return call<ReviewState>("POST", `/api/games/${encodeURIComponent(gameId)}/review`);
}

/** The review's bytes (mj.review.v1.Review), or null when there is none. */
export async function reviewBytes(gameId: string): Promise<Uint8Array | null> {
  const r = await fetch(`/api/games/${encodeURIComponent(gameId)}/review.pb`, { credentials: "same-origin" });
  if (!r.ok) return null;
  return new Uint8Array(await r.arrayBuffer());
}

/** Every key a record may be signed with (design 4 §8). */
export async function signingKeys(): Promise<PublishedKey[]> {
  const r = await fetch("/.well-known/mahjong-signing-keys.json");
  if (!r.ok) throw new Error(`signing keys: ${r.status}`);
  return (await r.json()) as PublishedKey[];
}
